import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import { useResizeObserver } from '@/hooks/use-resize-observer';
import type { EmptyStateWidgetProps } from '@/ui/widget/empty-state-widget';
import { EmptyStateWidget } from '@/ui/widget/empty-state-widget';
import { ReadOnlyEmptyWidget } from '@/ui/widget/read-only-empty-widget';
import type { WidgetProps } from '@/ui/widget/widget';
import { WIDGET_CONTAINER_PADDING_HORIZONTAL } from '@/ui/widget/widget';
import { Widget, WIDGET_CONTAINER_MAX_HEIGHT } from '@/ui/widget/widget';
import type { TextWidgetParagraphVariant } from './text-variant.type';
import { RichTextField } from '@/components/rich-text-field/rich-text-field';
import type PluginEditor from '@draft-js-plugins/editor/lib/Editor';

interface EditModeOptions {
  header?: React.ReactNode;
  backgroundColor?: string;
}

interface TextWidgetProps extends WidgetProps {
  paragraphVariant?: TextWidgetParagraphVariant;
  value: string | null;
  placeholder?: string;
  emptyStateOptions?: Omit<EmptyStateWidgetProps, 'onClick'>;
  editModeOptions?: EditModeOptions;
  onSave?: (value: string | null) => void;
  hideOnEmpty?: boolean;
  maxHeight?: number;
  disableBaseWidth?: boolean;
  dataTestId?: string;
}

const WIDGET_CONTAINER_PADDING_VERTICAL = 36;
const WIDGET_CONTAINER_DEFAULT_HEIGHT = 237;
export const WIDGET_CONTAINER_GAP = 12;

export const TextWidget = ({
  value,
  height = WIDGET_CONTAINER_DEFAULT_HEIGHT,
  paragraphVariant,
  emptyStateOptions,
  placeholder,
  disabled = false,
  onSave,
  className,
  backgroundColor,
  editModeOptions,
  header,
  showEditStamp,
  width,
  hideOnEmpty,
  maxHeight,
  disableBaseWidth = false,
  dataTestId,
  editable = true,
  ...widgetProps
}: TextWidgetProps) => {
  const { t } = useTranslation('projects');
  const [showReadMore, setShowReadMore] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [widgetText, setWidgetText] = useState(value);
  const maxHeightValue =
    isExpanded || isEditMode
      ? (maxHeight ?? WIDGET_CONTAINER_MAX_HEIGHT)
      : height;
  const backgroundColorValue =
    isEditMode && editModeOptions?.backgroundColor
      ? editModeOptions.backgroundColor
      : backgroundColor;
  const headerValue =
    isEditMode && editModeOptions?.header ? editModeOptions.header : header;

  const [baseWidth, setBaseWidth] = useState<number | undefined>();

  const editorRef = useRef<PluginEditor>(null);
  const richTextContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setIsExpanded(false);
  }, [isEditMode]);

  const ref = useResizeObserver(
    useCallback(
      (target: HTMLDivElement) => {
        setBaseWidth(currentWidth => {
          const nextWidth = target.offsetWidth;
          return currentWidth === nextWidth ? currentWidth : nextWidth;
        });

        const containerRect = target.getBoundingClientRect();
        const textArea = target.querySelector(
          '.DraftEditor-root',
        ) as HTMLDivElement | null;

        if (!textArea) return;

        const textRect = textArea.getBoundingClientRect();

        const visibleTop = Math.max(containerRect.top, textRect.top);
        const visibleBottom = Math.min(containerRect.bottom, textRect.bottom);

        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        const totalHeight = textArea.scrollHeight;

        const TOLERANCE = 2;

        const isClipped = totalHeight - visibleHeight > TOLERANCE;

        if (!isEditMode) {
          setShowReadMore(Boolean(widgetText) && (isClipped || isExpanded));
        }

        const richTextContainer = richTextContainerRef.current;
        if (!richTextContainer) return;

        const isScrollable =
          richTextContainer.scrollHeight > richTextContainer.clientHeight;

        if (isExpanded || isEditMode) {
          richTextContainer.style.maxHeight = `${
            maxHeightValue - WIDGET_CONTAINER_PADDING_VERTICAL * 2
          }px`;
          richTextContainer.style.overflowY = isScrollable ? 'auto' : '';
        } else {
          richTextContainer.style.maxHeight = '';
          richTextContainer.style.overflowY = '';
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [widgetText, isExpanded, isEditMode, height, value, maxHeightValue],
    ),
  );

  useEffect(() => {
    // force refresh default value on page load
    setWidgetText(value);
  }, [value]);

  const scrollToTextTop = useCallback(() => {
    const richTextContainer = richTextContainerRef.current;
    if (!richTextContainer) {
      return;
    }
    richTextContainer.scrollTop = 0;
  }, [richTextContainerRef]);

  const openEditMode = () => {
    setIsExpanded(false);
    setIsEditMode(true);

    editorRef.current?.focus();
  };

  const handleOnConfirm = () => {
    scrollToTextTop();
    widgetText !== value && onSave?.(widgetText);

    setIsEditMode(false);
  };

  const handleOnExpand = () => {
    scrollToTextTop();
    setIsExpanded(current => !current);
  };

  if (
    !isEditMode &&
    emptyStateOptions &&
    !disabled &&
    !widgetText &&
    editable
  ) {
    return (
      <EmptyStateWidget
        height={height}
        maxHeight={maxHeightValue}
        onEditClick={openEditMode}
        className={className}
        dataTestId={dataTestId}
        {...emptyStateOptions}
      />
    );
  }

  if (!widgetText && (disabled || !editable)) {
    return (
      <ReadOnlyEmptyWidget
        {...widgetProps}
        editable={editable}
        height={height}
        className={className}
        header={headerValue}
        maxHeight={maxHeightValue}
      />
    );
  }

  return (
    <Container
      ref={ref}
      {...widgetProps}
      {...(!disableBaseWidth && { width: baseWidth })}
      backgroundColor={backgroundColorValue}
      header={headerValue}
      className={className}
      height={height}
      isEditMode={isEditMode}
      showExpandButton={showReadMore && !isEditMode}
      disabled={disabled}
      labelExpand={t`projectOverview.readMore`}
      labelCollapse={t`projectOverview.seeLess`}
      onEditClick={openEditMode}
      maxHeight={maxHeightValue}
      onReadMoreClick={handleOnExpand}
      showMore={isExpanded}
      showEditStamp={!isEditMode && showEditStamp}
      showEditButton={editable}
      dataTestId={dataTestId}
    >
      {hideOnEmpty && value?.length === 0 ? null : (
        <RichTextField
          ref={richTextContainerRef}
          className="rich-text-field-container"
          width={
            width ? width - WIDGET_CONTAINER_PADDING_HORIZONTAL : undefined
          }
          onSave={handleOnConfirm}
          defaultValue={widgetText}
          placeholder={placeholder}
          paragraphVariant={paragraphVariant}
          onChange={newValue => setWidgetText(newValue)}
          readOnly={!isEditMode}
          editorRef={editorRef}
        />
      )}
    </Container>
  );
};

interface StyledWidgetProps extends WidgetProps {
  maxHeight?: number;
  width?: number;
}

const Container = styled(Widget, forwardRef)<StyledWidgetProps>`
  display: flex;
  flex-direction: column;
  gap: ${WIDGET_CONTAINER_GAP}px;
  height: ${({ showMore, height }) => (showMore ? 'auto' : `${height}px`)};
  ${({ maxHeight }) => maxHeight && `max-height: ${maxHeight}px`};
  ${({ width }) => width && `width: ${width}px;`}

  .rich-text-field-container {
    transition: max-height 0.3s ease;
    max-height: ${({ showMore, maxHeight }) =>
      showMore && maxHeight ? `${maxHeight}px` : 'none'};

    .public-DraftStyleDefault-ltr {
      text-align: justify;
    }
    .public-DraftStyleDefault-block {
      white-space: pre-wrap;
      word-break: break-word;
      line-height: 1.5;
    }
  }
`;
